import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import { useSnackbar } from 'notistack';
import ramoProvService, { RamoProv } from '../../services/ramoProvService';
import ImputableSearch from '../../components/ImputableSearch';

const defaultForm: RamoProv = {
  codigoramoprov: '',
  nombreramoprov: '',
  ctactasxpg: 0,
  ctacompras: 0,
  estadoramoprov: 'Activo'
};

/** CHAR/fijos en SQL devuelven espacios; alinear con la URL y el PUT. */
const normalizeRamoStrings = (ramo: RamoProv): RamoProv => ({
  ...ramo,
  codigoramoprov: ramo.codigoramoprov?.trim() ?? '',
  nombreramoprov: ramo.nombreramoprov?.trim() ?? '',
  estadoramoprov: (ramo.estadoramoprov?.trim() || 'Activo'),
});

const RamosProvForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoRamoProv } = useParams();
  const [formData, setFormData] = useState<RamoProv>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [ctaCtasXPgSearchOpen, setCtaCtasXPgSearchOpen] = useState(false);
  const [ctaComprasSearchOpen, setCtaComprasSearchOpen] = useState(false);

  const loadRamoProv = useCallback(async () => {
    if (!codigoRamoProv || codigoRamoProv === 'nuevo') return;
    try {
      setLoading(true);
      const ramo = await ramoProvService.getRamoProv(codigoRamoProv);
      const normalized = normalizeRamoStrings(ramo);
      const canonical = normalized.codigoramoprov;
      if (canonical && canonical !== codigoRamoProv.trim()) {
        navigate(`/compras/ramosprov/${encodeURIComponent(canonical)}`, { replace: true });
      }
      setFormData(normalized);
    } catch (error) {
      enqueueSnackbar('Error al cargar el ramo de proveedor', { variant: 'error' });
      navigate('/compras/ramosprov');
    } finally {
      setLoading(false);
    }
  }, [codigoRamoProv, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoRamoProv && codigoRamoProv !== 'nuevo') {
      loadRamoProv();
    }
  }, [codigoRamoProv, loadRamoProv]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'ctactasxpg' || name === 'ctacompras') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImputableCtasXPgSelect = (imputable: any) => {
    setFormData(prev => ({
      ...prev,
      ctactasxpg: imputable.nroCuenta
    }));
    setCtaCtasXPgSearchOpen(false);
  };

  const handleImputableComprasSelect = (imputable: any) => {
    setFormData(prev => ({
      ...prev,
      ctacompras: imputable.nroCuenta
    }));
    setCtaComprasSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      const payload = normalizeRamoStrings(formData);
      if (!payload.codigoramoprov || !payload.nombreramoprov) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      if (codigoRamoProv && codigoRamoProv !== 'nuevo') {
        await ramoProvService.updateRamoProv(payload.codigoramoprov, payload);
      } else {
        await ramoProvService.createRamoProv(payload);
      }

      enqueueSnackbar('Ramo de proveedor guardado exitosamente', { variant: 'success' });
      navigate('/compras/ramosprov');
    } catch (error: any) {
      console.error('Error al guardar ramo de proveedor:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el ramo de proveedor', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {codigoRamoProv && codigoRamoProv !== 'nuevo' ? 'Editar Ramo de Proveedor' : 'Nuevo Ramo de Proveedor'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Contabilidad" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="codigoramoprov" 
                    label="Código" 
                    value={formData.codigoramoprov || ''} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!codigoRamoProv && codigoRamoProv !== 'nuevo'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField 
                    name="nombreramoprov" 
                    label="Nombre" 
                    value={formData.nombreramoprov || ''} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    name="estadoramoprov"
                    label="Estado"
                    value={formData.estadoramoprov || 'Activo'}
                    onChange={(v) => setFormData(prev => ({ ...prev, estadoramoprov: v ?? 'Activo' }))}
                    options={[
                      { value: 'Activo', label: 'Activo' },
                      { value: 'Inactivo', label: 'Inactivo' },
                    ]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Cuenta Cuentas x Pagar" 
                      value={formData.ctactasxpg || ''} 
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setCtaCtasXPgSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Cuenta Compras" 
                      value={formData.ctacompras || ''} 
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setCtaComprasSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/compras/ramosprov')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ImputableSearch
        open={ctaCtasXPgSearchOpen}
        onClose={() => setCtaCtasXPgSearchOpen(false)}
        onSelect={handleImputableCtasXPgSelect}
      />

      <ImputableSearch
        open={ctaComprasSearchOpen}
        onClose={() => setCtaComprasSearchOpen(false)}
        onSelect={handleImputableComprasSelect}
      />
    </Box>
  );
};

export default RamosProvForm;
